import React, { useContext, useEffect, useMemo, useState } from "react";
import { FhirClientContext } from "../FhirClientContext";
import "./Chart.css";

const TABS = [
    { key: "providers", label: "Providers", resourceType: "PractitionerRole", query: () => "PractitionerRole?_count=50" },
    { key: "allergies", label: "Allergies", resourceType: "AllergyIntolerance", query: (id) => `AllergyIntolerance?patient=${id}&_sort=-date&_count=50` },
    { key: "careTeam", label: "Care Team", resourceType: "CareTeam", query: (id) => `CareTeam?patient=${id}&_count=50` },
    { key: "claims", label: "Claims", resourceType: "ExplanationOfBenefit", query: (id) => `ExplanationOfBenefit?patient=${id}&_sort=-created&_count=50` },
    { key: "coreVitals", label: "Core Vitals", resourceType: "Observation", query: (id) => `Observation?patient=${id}&category=vital-signs&_sort=-date&_count=100` },
    { key: "diagnoses", label: "Diagnoses", resourceType: "Condition", query: (id) => `Condition?patient=${id}&_sort=-recorded-date&_count=100` },
    { key: "diagnosticReports", label: "Diagnostic Reports", resourceType: "DiagnosticReport", query: (id) => `DiagnosticReport?patient=${id}&_sort=-date&_count=100` },
    { key: "encounters", label: "Encounters", resourceType: "Encounter", query: (id) => `Encounter?patient=${id}&_sort=-date&_count=100` },
    { key: "planDetails", label: "Plan Details", resourceType: "Coverage", query: (id) => `Coverage?patient=${id}&_count=50` },
    { key: "prescriptions", label: "Prescriptions", resourceType: "MedicationRequest", query: (id) => `MedicationRequest?patient=${id}&_sort=-authoredon&_count=100` },
    { key: "dispenses", label: "Dispenses", resourceType: "MedicationDispense", query: (id) => `MedicationDispense?patient=${id}&_sort=-whenhandedover&_count=100` },
    { key: "procedures", label: "Procedures", resourceType: "Procedure", query: (id) => `Procedure?patient=${id}&_sort=-date&_count=100` },
    { key: "vaccinations", label: "Vaccinations", resourceType: "Immunization", query: (id) => `Immunization?patient=${id}&_sort=-date&_count=100` }
];

function text(value, fallback = "Not available") {
    return value ? String(value) : fallback;
}

function codeableConceptText(concept) {
    if (!concept) {
        return "Not available";
    }

    if (concept.text) {
        return concept.text;
    }

    const firstCoding = Array.isArray(concept.coding) ? concept.coding[0] : null;
    return firstCoding?.display || firstCoding?.code || "Not available";
}

function referenceText(reference) {
    return reference?.display || reference?.reference || "Not available";
}

function periodText(period) {
    if (!period) {
        return "Not available";
    }

    const start = period.start || "?";
    const end = period.end || "ongoing";
    return `${start} to ${end}`;
}

function summarize(resourceType, resource) {
    switch (resourceType) {
        case "PractitionerRole":
            return {
                title: referenceText(resource.practitioner),
                details: [
                    `Organization: ${referenceText(resource.organization)}`,
                    `Specialty: ${(resource.specialty || []).map(codeableConceptText).join(", ") || "Not available"}`,
                    `Telecom: ${(resource.telecom || []).map((t) => `${text(t.system)}: ${text(t.value)}`).join(", ") || "Not available"}`
                ]
            };
        case "AllergyIntolerance":
            return {
                title: codeableConceptText(resource.code),
                details: [
                    `Criticality: ${text(resource.criticality, "unknown")}`,
                    `Clinical Status: ${codeableConceptText(resource.clinicalStatus)}`,
                    `Recorded: ${text(resource.recordedDate)}`
                ]
            };
        case "CareTeam":
            return {
                title: text(resource.name, "Care Team"),
                details: [
                    `Status: ${text(resource.status, "unknown")}`,
                    `Subject: ${referenceText(resource.subject)}`,
                    `Participants: ${(resource.participant || []).map((p) => referenceText(p.member)).slice(0, 4).join(", ") || "Not available"}`
                ]
            };
        case "ExplanationOfBenefit":
            return {
                title: codeableConceptText(resource.type),
                details: [
                    `Status: ${text(resource.status, "unknown")}`,
                    `Created: ${text(resource.created)}`,
                    `Outcome: ${text(resource.outcome)}`
                ]
            };
        case "Observation":
            return {
                title: codeableConceptText(resource.code),
                details: [
                    `Value: ${resource.valueQuantity ? `${resource.valueQuantity.value || ""} ${resource.valueQuantity.unit || ""}`.trim() : text(resource.valueString || resource.valueCodeableConcept?.text)}`,
                    `Effective: ${text(resource.effectiveDateTime || resource.issued)}`,
                    `Category: ${(resource.category || []).map(codeableConceptText).join(", ") || "Not available"}`
                ]
            };
        case "Condition":
            return {
                title: codeableConceptText(resource.code),
                details: [
                    `Clinical Status: ${codeableConceptText(resource.clinicalStatus)}`,
                    `Verification: ${codeableConceptText(resource.verificationStatus)}`,
                    `Onset: ${text(resource.onsetDateTime || resource.recordedDate)}`
                ]
            };
        case "DiagnosticReport":
            return {
                title: codeableConceptText(resource.code),
                details: [
                    `Status: ${text(resource.status, "unknown")}`,
                    `Category: ${(resource.category || []).map(codeableConceptText).join(", ") || "Not available"}`,
                    `Effective: ${text(resource.effectiveDateTime || resource.issued)}`
                ]
            };
        case "Encounter":
            return {
                title: codeableConceptText(resource.type?.[0]),
                details: [
                    `Status: ${text(resource.status, "unknown")}`,
                    `Class: ${text(resource.class?.display || resource.class?.code)}`,
                    `Period: ${periodText(resource.period)}`
                ]
            };
        case "Coverage":
            return {
                title: codeableConceptText(resource.type),
                details: [
                    `Status: ${text(resource.status, "unknown")}`,
                    `Subscriber: ${referenceText(resource.subscriber)}`,
                    `Period: ${periodText(resource.period)}`
                ]
            };
        case "MedicationRequest":
            return {
                title: codeableConceptText(resource.medicationCodeableConcept),
                details: [
                    `Status: ${text(resource.status, "unknown")}`,
                    `Intent: ${text(resource.intent, "unknown")}`,
                    `Authored On: ${text(resource.authoredOn)}`
                ]
            };
        case "MedicationDispense":
            return {
                title: codeableConceptText(resource.medicationCodeableConcept),
                details: [
                    `Status: ${text(resource.status, "unknown")}`,
                    `When Handed Over: ${text(resource.whenHandedOver)}`,
                    `Performer: ${referenceText(resource.performer?.[0]?.actor)}`
                ]
            };
        case "Procedure":
            return {
                title: codeableConceptText(resource.code),
                details: [
                    `Status: ${text(resource.status, "unknown")}`,
                    `Performed: ${text(resource.performedDateTime || resource.performedPeriod?.start)}`,
                    `Performer: ${referenceText(resource.performer?.[0]?.actor)}`
                ]
            };
        case "Immunization":
            return {
                title: codeableConceptText(resource.vaccineCode),
                details: [
                    `Status: ${text(resource.status, "unknown")}`,
                    `Occurrence: ${text(resource.occurrenceDateTime)}`,
                    `Primary Source: ${text(resource.primarySource)}`
                ]
            };
        default:
            return {
                title: text(resource.resourceType),
                details: ["No summary available"]
            };
    }
}

export default function Chart({ patientId }) {
    const { client } = useContext(FhirClientContext);
    const [activeTab, setActiveTab] = useState(TABS[0].key);
    const [data, setData] = useState({});
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!patientId) {
            return;
        }

        let cancelled = false;
        setIsLoading(true);

        Promise.all(
            TABS.map(async (tab) => {
                try {
                    const resources = await client.request(tab.query(patientId), { pageLimit: 0, flat: true });
                    return { key: tab.key, resources: Array.isArray(resources) ? resources : [] };
                } catch (error) {
                    return { key: tab.key, error };
                }
            })
        ).then((results) => {
            if (cancelled) {
                return;
            }

            const nextData = {};
            const nextErrors = {};

            results.forEach((result) => {
                if (result.error) {
                    nextErrors[result.key] = result.error;
                    nextData[result.key] = [];
                } else {
                    nextData[result.key] = result.resources;
                }
            });

            setData(nextData);
            setErrors(nextErrors);
            setIsLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, [client, patientId]);

    const currentTab = useMemo(() => TABS.find((tab) => tab.key === activeTab) || TABS[0], [activeTab]);
    const currentResources = data[currentTab.key] || [];
    const currentError = errors[currentTab.key];

    return (
        <div className="clinical-tabs">
            <aside className="clinical-tabs__menu" role="tablist" aria-label="Clinical data tabs">
                {TABS.map((tab) => {
                    const count = (data[tab.key] || []).length;
                    const selected = tab.key === activeTab;
                    return (
                        <button
                            key={tab.key}
                            className={`clinical-tab ${selected ? "clinical-tab--active" : ""}`}
                            onClick={() => setActiveTab(tab.key)}
                            role="tab"
                            aria-selected={selected}
                            type="button"
                        >
                            <span className="clinical-tab__label">{tab.label}</span>
                            <span className="clinical-tab__count">{count}</span>
                        </button>
                    );
                })}
            </aside>

            <section className="clinical-tabs__content" role="tabpanel">
                <header className="clinical-tabs__content-header">
                    <h2>{currentTab.label}</h2>
                    <span>{currentResources.length} item(s)</span>
                </header>

                <div className="clinical-tabs__content-body">
                    {isLoading && <p className="tab-message">Loading clinical data...</p>}
                    {!isLoading && currentError && (
                        <p className="tab-message tab-message--error">{currentError.message}</p>
                    )}
                    {!isLoading && !currentError && currentResources.length === 0 && (
                        <p className="tab-message">No {currentTab.label.toLowerCase()} were returned for this patient.</p>
                    )}
                    {!isLoading && !currentError && currentResources.length > 0 && (
                        <ul className="clinical-list">
                            {currentResources.map((resource, index) => {
                                const summary = summarize(currentTab.resourceType, resource);
                                return (
                                    <li className="clinical-item" key={`${resource.id || "row"}-${index}`}>
                                        <h3>{summary.title}</h3>
                                        {summary.details.map((detail, detailIndex) => (
                                            <p key={detailIndex}>{detail}</p>
                                        ))}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </section>
        </div>
    );
}
